export interface CameraKeeper<K, T> {
  acquire(key: K, open: () => Promise<T>): Promise<T>;
  release(): void;
}

function isLive(stream: MediaStream): boolean {
  const tracks = stream.getTracks();
  return tracks.length > 0 && tracks.every((track) => track.readyState === "live");
}

function stopStream(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) {
    track.stop();
  }
}

export function createCameraKeeper<K, T>(
  streamOf: (value: T) => MediaStream | null,
): CameraKeeper<K, T> {
  let held: { key: K; pending: Promise<T> } | null = null;

  const release = (): void => {
    const current = held;
    held = null;
    void current?.pending.then(
      (value) => stopStream(streamOf(value)),
      () => undefined,
    );
  };

  const start = (key: K, open: () => Promise<T>): Promise<T> => {
    release();
    const entry = { key, pending: open() };
    held = entry;
    const forget = (): void => {
      if (held === entry) {
        held = null;
      }
    };
    entry.pending.then((value) => {
      if (streamOf(value) === null) {
        forget();
      }
    }, forget);
    return entry.pending;
  };

  const acquire = (key: K, open: () => Promise<T>): Promise<T> => {
    const current = held;
    if (current === null || current.key !== key) {
      return start(key, open);
    }
    return current.pending.then((value) => {
      const stream = streamOf(value);
      if (stream === null || isLive(stream)) {
        return value;
      }
      return held === current ? start(key, open) : acquire(key, open);
    });
  };

  return { acquire, release };
}
